import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import {
  VaRadio,
  VaRadioOption,
} from '@department-of-veterans-affairs/component-library/dist/react-bindings';
import { WIZARD_STATUS_NOT_STARTED } from 'platform/site-wide/wizard';

import SubTaskContainerHeader from '../SubTaskContainerHeader';
import useSubtaskStatusMarker from '../useSubtaskStatusMarker';
import { usePathHistory } from '../utils/tracking/PathHistoryContext';
import {
  trackScreenerStepView,
  trackScreenerViewedWithInProgressForm,
  trackMilitaryStatusSelection,
  trackScreenerValidationError,
} from '../../utils/tracking/features/screenerTracking';
import pageNames from './pageNames';

const dataKey = 'view:militaryStatus';

const content = {
  label: 'Are you on active duty right now?',
  errorMessage: 'Select a response',
};

const options = [
  { value: 'active-duty', label: 'Yes' },
  { value: 'veteran', label: 'No' },
];

const optionValues = options.map(option => option.value);

const validate = data => optionValues.includes(data[dataKey]);

// Active-duty service members branch into the BDD (Benefits Delivery at
// Discharge) flow; everyone else continues through the standard screener.
const getNextPage = data =>
  data[dataKey] === 'active-duty' ? pageNames.releaseDate : pageNames.claimType;

/**
 * Start 1 — Military status screener
 * @param {Object} data - subtask data
 * @param {Boolean} error - page submitted & error state
 * @param {Function} setPageData - updates subtask data
 * @returns {JSX}
 */
const MilitaryStatusPage = ({ data = {}, error, setPageData }) => {
  const { addStep, getHistory } = usePathHistory();
  const savedForms = useSelector(
    state => state?.user?.profile?.savedForms || [],
  );

  // Entering the screener resets any prior completion so the introduction
  // page shows the screener link again until the flow is finished.
  useSubtaskStatusMarker(WIZARD_STATUS_NOT_STARTED);

  useEffect(() => {
    trackScreenerStepView({
      stepName: pageNames.militaryStatus,
      pathHistoryContext: { addStep, getHistory },
    });
    trackScreenerViewedWithInProgressForm({ savedForms });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (error) {
      trackScreenerValidationError({
        pageName: pageNames.militaryStatus,
        fieldName: 'military-status',
        errorMessage: content.errorMessage,
      });
    }
  }, [error]);

  const handleSelect = ({ target }) => {
    setPageData({ [dataKey]: target.value || null });
  };

  return (
    <>
      <SubTaskContainerHeader />
      <VaRadio
        label={content.label}
        labelHeaderLevel="2"
        error={error ? content.errorMessage : null}
        onRadioOptionSelected={handleSelect}
        className="vads-u-margin-top--0"
        required
      >
        {options.map(({ value, label }) => (
          <VaRadioOption
            key={value}
            className="vads-u-margin-y--2"
            name="militaryStatus"
            id={value}
            value={value}
            label={label}
            checked={value === data[dataKey]}
          />
        ))}
      </VaRadio>
    </>
  );
};

MilitaryStatusPage.propTypes = {
  data: PropTypes.shape({}),
  error: PropTypes.bool,
  setPageData: PropTypes.func,
};

export default {
  name: pageNames.militaryStatus,
  component: MilitaryStatusPage,
  validate,
  back: null,
  next: getNextPage,
  onContinue: data => trackMilitaryStatusSelection(data[dataKey]),
};
